import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Routes, Route, useParams } from "react-router-dom";
import { api, eur, API, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import { LayoutDashboard, ShoppingBag, Scissors, Sparkles, Users, Download, Upload, ArrowLeft, Trash2, Edit3, Plus, CheckCircle2 } from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", Icon: LayoutDashboard, test: "nav-dashboard" },
  { to: "/admin/ordini", label: "Ordini", Icon: ShoppingBag, test: "nav-orders" },
  { to: "/admin/servizi", label: "Servizi", Icon: Scissors, test: "nav-services" },
  { to: "/admin/prodotti", label: "Prodotti", Icon: Sparkles, test: "nav-products" },
  { to: "/admin/clienti", label: "Clienti", Icon: Users, test: "nav-customers" },
];

function AdminShell({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-10">Caricamento…</div>;
  if (user === false || user.role !== "admin") return <Navigate to="/accedi?redirect=/admin" replace />;

  return (
    <div className="min-h-screen bg-[#F4F1EA]/50" data-testid="admin-shell">
      <div className="max-w-[1600px] mx-auto flex">
        <aside className="w-60 shrink-0 border-r border-[#E4DFD5] min-h-screen bg-white sticky top-0 hidden md:block">
          <div className="p-6 border-b border-[#E4DFD5]">
            <div className="font-serif text-xl">Premium Shoes</div>
            <div className="overline text-[#B97A48] text-[0.65rem]">Admin Console</div>
          </div>
          <nav className="p-3 space-y-1">
            {NAV.map(({ to, end, label, Icon, test }) => (
              <NavLink key={to} to={to} end={end} className={({ isActive }) => `admin-nav-item ${isActive ? "active" : ""}`} data-testid={test}>
                <Icon size={16} strokeWidth={1.5} /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 mt-4 border-t border-[#E4DFD5] text-xs text-[#5C5A56]">
            <div>{user.email}</div>
            <Link to="/" className="link-underline mt-2 inline-block">← Torna al sito</Link>
          </div>
        </aside>
        <main className="flex-1 min-w-0 p-6 md:p-10">{children}</main>
      </div>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="p-5 bg-white border border-[#E4DFD5]">
      <div className="overline text-[0.65rem]">{label}</div>
      <div className="font-serif text-3xl mt-2">{value}</div>
      {hint && <div className="text-xs text-[#5C5A56] mt-1">{hint}</div>}
    </div>
  );
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data.slice(0, 8)));
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-semibold">Dashboard</h1>
      <p className="text-sm text-[#5C5A56] mt-1">Panoramica attività atelier</p>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Stat label="Fatturato" value={eur(stats.revenue_eur)} hint={`${stats.paid_orders} ordini pagati`} />
          <Stat label="Ordini totali" value={stats.total_orders} />
          <Stat label="Clienti" value={stats.total_customers} />
          <Stat label="In lavorazione" value={stats.status_counts.in_lavorazione || 0} hint="da completare" />
        </div>
      )}

      {stats && (
        <div className="mt-8 p-5 bg-white border border-[#E4DFD5]">
          <div className="overline mb-3">Pipeline ordini</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {Object.entries(stats.status_counts).map(([k, v]) => (
              <div key={k} className="p-3 bg-[#F4F1EA] border border-[#E4DFD5]">
                <div className="text-xs text-[#5C5A56] capitalize">{k.replace("_", " ")}</div>
                <div className="font-serif text-2xl mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Ultimi ordini</h2>
          <Link to="/admin/ordini" className="text-sm link-underline">Vedi tutti →</Link>
        </div>
        <div className="mt-4 bg-white border border-[#E4DFD5]">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Ordine</th><th>Cliente</th><th>Tipo</th><th>Totale</th><th>Pagamento</th><th>Stato</th><th>Data</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td className="font-mono text-xs">#{o.id.slice(0, 8).toUpperCase()}</td>
                  <td>{o.user_name}<br /><span className="text-xs text-[#5C5A56]">{o.user_email}</span></td>
                  <td className="capitalize">{o.order_type}</td>
                  <td className="font-medium">{eur(o.total)}</td>
                  <td><StatusBadge status={o.payment_status} /></td>
                  <td><StatusBadge status={o.status} /></td>
                  <td className="text-xs">{new Date(o.created_at).toLocaleDateString("it-IT")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const STATUS_OPTIONS = ["ricevuto", "in_lavorazione", "pronto", "spedito", "consegnato", "annullato"];

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("tutti");

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    load();
  };

  const visible = filter === "tutti" ? orders : orders.filter((o) => o.status === filter);

  const downloadLabel = async (o) => {
    const token = localStorage.getItem("psc_token");
    const res = await fetch(`${API}/orders/${o.id}/label`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { alert("Etichetta non disponibile"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = o.label_filename || `etichetta-${o.tracking_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadLabel = async (o, file) => {
    if (!file) return;
    const token = localStorage.getItem("psc_token");
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/admin/orders/${o.id}/label`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert("Errore upload: " + (err.detail || res.status));
      return;
    }
    await load();
  };

  const removeLabel = async (o) => {
    if (!window.confirm(`Rimuovere l'etichetta caricata per #${o.id.slice(0,8).toUpperCase()}?`)) return;
    const token = localStorage.getItem("psc_token");
    await fetch(`${API}/admin/orders/${o.id}/label`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    await load();
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="text-2xl font-semibold">Ordini</h1>
      <p className="text-sm text-[#5C5A56] mt-1">Gestisci lo stato e le etichette di spedizione</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {["tutti", ...STATUS_OPTIONS].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm ${filter === s ? "bg-[#1C1B1A] text-white" : "bg-white border border-[#E4DFD5] text-[#5C5A56] hover:text-[#1C1B1A]"}`} data-testid={`filter-${s}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-white border border-[#E4DFD5] overflow-x-auto">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Ordine</th><th>Cliente</th><th>Tipo</th><th>Totale</th><th>Pagamento</th><th>Tracking</th><th>Stato</th><th>Etichetta</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((o) => (
              <tr key={o.id} data-testid={`admin-order-${o.id}`}>
                <td className="font-mono text-xs">#{o.id.slice(0, 8).toUpperCase()}<br /><span className="text-[#5C5A56]">{new Date(o.created_at).toLocaleDateString("it-IT")}</span></td>
                <td>{o.user_name}<br /><span className="text-xs text-[#5C5A56]">{o.user_email}</span></td>
                <td className="capitalize">{o.order_type}</td>
                <td className="font-medium">{eur(o.total)}</td>
                <td><StatusBadge status={o.payment_status} /></td>
                <td className="font-mono text-xs">{o.tracking_number}</td>
                <td>
                  <select className="input-field !py-1 !px-2 text-xs" value={o.status} onChange={(e) => changeStatus(o.id, e.target.value)} data-testid={`status-select-${o.id}`}>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </td>
                <td>
                  {o.has_label ? (
                    <div className="flex items-center gap-1" data-testid={`label-uploaded-${o.id}`}>
                      <button onClick={() => downloadLabel(o)} className="p-2 hover:bg-[#F4F1EA] text-[#4A6B53]" title="Scarica etichetta caricata" data-testid={`label-download-${o.id}`}>
                        <Download size={14} strokeWidth={1.5} />
                      </button>
                      <label className="p-2 hover:bg-[#F4F1EA] cursor-pointer text-[#5C5A56]" title="Sostituisci etichetta">
                        <Upload size={14} strokeWidth={1.5} />
                        <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={(e) => uploadLabel(o, e.target.files?.[0])} data-testid={`label-upload-${o.id}`} />
                      </label>
                      <button onClick={() => removeLabel(o)} className="p-2 hover:bg-[#F4F1EA] text-[#9E413B]" title="Rimuovi etichetta" data-testid={`label-delete-${o.id}`}>
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                      <CheckCircle2 size={14} strokeWidth={1.8} className="text-[#4A6B53] ml-1" />
                    </div>
                  ) : (
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 cursor-pointer text-xs uppercase tracking-widest border border-[#1C1B1A] hover:bg-[#1C1B1A] hover:text-white transition-colors" title="Carica etichetta (PDF/PNG/JPG)" data-testid={`label-upload-${o.id}`}>
                      <Upload size={12} strokeWidth={1.6} /> Carica
                      <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={(e) => uploadLabel(o, e.target.files?.[0])} />
                    </label>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminItemsCRUD({ resource, title, fields }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null, 'new', or item obj
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");

  const load = () => api.get(`/${resource}?include_inactive=true`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [resource]);

  const openNew = () => {
    const init = {};
    fields.forEach((f) => init[f.key] = f.default ?? "");
    init.active = true;
    setForm(init); setEditing("new"); setErr("");
  };
  const openEdit = (it) => { setForm({ ...it }); setEditing(it); setErr(""); };

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = {};
      fields.forEach((f) => {
        payload[f.key] = f.type === "number" ? parseFloat(form[f.key]) || 0 :
                        f.type === "int" ? parseInt(form[f.key]) || 0 :
                        form[f.key];
      });
      payload.active = form.active ?? true;
      if (editing === "new") {
        await api.post(`/${resource}`, payload);
      } else {
        await api.put(`/${resource}/${editing.id}`, payload);
      }
      setEditing(null); load();
    } catch (ex) { setErr(formatError(ex)); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare "${it.name}"?`)) return;
    await api.delete(`/${resource}/${it.id}`);
    load();
  };

  return (
    <div data-testid={`admin-${resource}`}>
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-sm text-[#5C5A56] mt-1">Crea, modifica, disattiva</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="new-item-btn"><Plus size={14} /> Nuovo</button>
      </div>

      <div className="mt-6 bg-white border border-[#E4DFD5] overflow-x-auto">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Nome</th><th>Slug</th>{resource === "services" ? <th>Prezzo da</th> : <><th>Prezzo</th><th>Stock</th></>}<th>Cat.</th><th>Attivo</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td className="font-medium">{it.name}</td>
                <td className="font-mono text-xs">{it.slug}</td>
                {resource === "services" ? <td>{eur(it.price_from)}</td> : <><td>{eur(it.price)}</td><td>{it.stock}</td></>}
                <td className="capitalize">{it.category}</td>
                <td>{it.active ? <span className="text-[#4A6B53]">●</span> : <span className="text-[#9E413B]">●</span>}</td>
                <td>
                  <button onClick={() => openEdit(it)} className="p-2 hover:bg-[#F4F1EA]" data-testid={`edit-${it.slug}`}><Edit3 size={14} strokeWidth={1.5} /></button>
                  <button onClick={() => remove(it)} className="p-2 hover:bg-[#F4F1EA] text-[#9E413B]" data-testid={`delete-${it.slug}`}><Trash2 size={14} strokeWidth={1.5} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-[#E4DFD5] p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="edit-modal">
            <h2 className="font-serif text-2xl mb-5">{editing === "new" ? `Nuovo ${title.slice(0, -1).toLowerCase()}` : `Modifica: ${editing.name}`}</h2>
            <div className="space-y-3">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="text-xs text-[#5C5A56] block mb-1">{f.label}</label>
                  {f.textarea ? (
                    <textarea className="input-field" rows={3} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                  ) : (
                    <input className="input-field" type={f.type === "number" || f.type === "int" ? "number" : "text"} step={f.type === "number" ? "0.01" : undefined} required={f.required} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                  )}
                </div>
              ))}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active ?? true} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Attivo
              </label>
            </div>
            {err && <div className="mt-3 p-3 bg-[#9E413B]/10 text-[#9E413B] text-xs border border-[#9E413B]/30">{err}</div>}
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="btn-secondary">Annulla</button>
              <button type="submit" className="btn-primary" data-testid="save-item-btn">Salva</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const SERVICE_FIELDS = [
  { key: "slug", label: "Slug (es. pulizia-completa)", required: true },
  { key: "name", label: "Nome", required: true },
  { key: "description", label: "Descrizione", textarea: true, required: true },
  { key: "price_from", label: "Prezzo da (€)", type: "number", required: true },
  { key: "duration_days", label: "Giorni di lavorazione", type: "int", default: 3 },
  { key: "image_url", label: "URL immagine" },
  { key: "category", label: "Categoria", default: "service" },
];
const PRODUCT_FIELDS = [
  { key: "slug", label: "Slug", required: true },
  { key: "name", label: "Nome", required: true },
  { key: "description", label: "Descrizione", textarea: true, required: true },
  { key: "price", label: "Prezzo (€)", type: "number", required: true },
  { key: "stock", label: "Stock", type: "int", default: 100 },
  { key: "image_url", label: "URL immagine" },
  { key: "category", label: "Categoria" },
];

function AdminServices() { return <AdminItemsCRUD resource="services" title="Servizi" fields={SERVICE_FIELDS} />; }
function AdminProducts() { return <AdminItemsCRUD resource="products" title="Prodotti" fields={PRODUCT_FIELDS} />; }

function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)); }, []);
  return (
    <div data-testid="admin-customers">
      <h1 className="text-2xl font-semibold">Clienti</h1>
      <p className="text-sm text-[#5C5A56] mt-1">Registro clienti registrati</p>
      <div className="mt-6 bg-white border border-[#E4DFD5]">
        <table className="adm-table">
          <thead><tr><th>Nome</th><th>Email</th><th>Telefono</th><th>Registrato</th></tr></thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id}>
                <td className="font-medium">{c.name}</td>
                <td>{c.email}</td>
                <td>{c.phone || "—"}</td>
                <td className="text-xs">{new Date(c.created_at).toLocaleDateString("it-IT")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <AdminShell>
      <Routes>
        <Route index element={<AdminDashboard />} />
        <Route path="ordini" element={<AdminOrders />} />
        <Route path="servizi" element={<AdminServices />} />
        <Route path="prodotti" element={<AdminProducts />} />
        <Route path="clienti" element={<AdminCustomers />} />
      </Routes>
    </AdminShell>
  );
}
